import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FONT_FAMILY } from "../common/sharedFunctions";
import PageSEO from '../components/SEO/PageSEO';

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const navigate = useNavigate();

  const sections = [
    { heading: t('privacy_policy_heading_info'), paragraphs: [t('privacy_policy_info_para1')], lists: [t('privacy_policy_info_list1'), t('privacy_policy_info_list2'), t('privacy_policy_info_list3'), t('privacy_policy_info_list4')], after: [t('privacy_policy_info_para2'), t('privacy_policy_info_para3'), t('privacy_policy_info_para4')], lists2: [t('privacy_policy_info_list5'), t('privacy_policy_info_list6'), t('privacy_policy_info_list7')], after2: [t('privacy_policy_info_para5')] },
    { heading: t('privacy_policy_heading_log'), paragraphs: [t('privacy_policy_log_para1')] },
    { heading: t('privacy_policy_heading_cookie'), paragraphs: [t('privacy_policy_cookie_para1'), t('privacy_policy_cookie_para2')] },
    { heading: t('privacy_policy_heading_service'), paragraphs: [t('privacy_policy_service_para1')], lists: [t('privacy_policy_service_list1'), t('privacy_policy_service_list2'), t('privacy_policy_service_list3'), t('privacy_policy_service_list4')], after: [t('privacy_policy_service_para2')] },
    { heading: t('privacy_policy_heading_security'), paragraphs: [t('privacy_policy_security_para1')] },
    { heading: t('privacy_policy_heading_link'), paragraphs: [t('privacy_policy_link_para1')] },
    { heading: t('privacy_policy_heading_children'), paragraphs: [t('privacy_policy_children_para1')] },
    { heading: t('delete_account_lebel'), paragraphs: [t('delete_account_msg'), t('delete_account_subheading')], lists: [t('delete_account_para1'), t('delete_account_para2')] },
    { heading: t('privacy_policy_heading_change_privacy'), paragraphs: [t('privacy_policy_change_privacy_para1'), t('privacy_policy_change_privacy_para2')] },
    { heading: t('privacy_policy_heading_contact'), paragraphs: [t('privacy_policy_contact_para1')] },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#009689]/5 via-white to-[#319F44]/5" style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <PageSEO
        title="Privacy Policy"
        description="Gruwcer Delivery Privacy Policy - Learn how we collect, use, and protect your personal information when using our delivery services."
        keywords="privacy policy, data protection, user privacy, Gruwcer Delivery policy, delivery privacy"
        path="/privacy-policy"
      />
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <img src="/gruwcer-logo.png" alt="Gruwcer Delivery" onClick={() => navigate('/')} className="h-8 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity" />
          </div>
        </div>
      </header>

      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-gray-900 mb-4 text-center"
            style={{ fontFamily: FONT_FAMILY }}
          >
            {t('privacy_policy')}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-600 text-center mb-12 space-y-2"
          >
            <p style={{ fontFamily: FONT_FAMILY }}>{t('privacy_policy_para1')}</p>
            <p style={{ fontFamily: FONT_FAMILY }}>{t('privacy_policy_para2')}</p>
            <p style={{ fontFamily: FONT_FAMILY }}>{t('privacy_policy_para3')}</p>
          </motion.div>

          <div className="space-y-8">
            {sections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: FONT_FAMILY }}>
                  {section.heading}
                </h3>
                {section.paragraphs?.map((p, j) => (
                  <p key={j} className="text-sm text-gray-600 mb-2 leading-relaxed" style={{ fontFamily: FONT_FAMILY }}>{p}</p>
                ))}
                {section.lists && (
                  <ul className="list-disc list-inside text-sm text-gray-600 mb-2 space-y-1 ml-2">
                    {section.lists.map((item, j) => <li key={j} style={{ fontFamily: FONT_FAMILY }}>{item}</li>)}
                  </ul>
                )}
                {section.after?.map((p, j) => (
                  <p key={j} className="text-sm text-gray-600 mb-2 leading-relaxed" style={{ fontFamily: FONT_FAMILY }}>{p}</p>
                ))}
                {section.lists2 && (
                  <ul className="list-disc list-inside text-sm text-gray-600 mb-2 space-y-1 ml-2">
                    {section.lists2.map((item, j) => <li key={j} style={{ fontFamily: FONT_FAMILY }}>{item}</li>)}
                  </ul>
                )}
                {section.after2?.map((p, j) => (
                  <p key={j} className="text-sm text-gray-600 mb-2 leading-relaxed" style={{ fontFamily: FONT_FAMILY }}>{p}</p>
                ))}
                {section.heading === t('privacy_policy_heading_contact') && settings?.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="text-sm text-[#009689] font-medium hover:underline" style={{ fontFamily: FONT_FAMILY }}>
                    {settings.contact_email}
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
